import axios from 'axios';

import {
  SubscriptionDto,
  SubscriptionStatus,
  parseSubscriptionTier,
  parseSubscriptionStatus,
  subscriptionTierDetails,
} from '../models/Subscription';
import { logApiError } from '../utils/errorUtils';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';
import { resolveTierPriceId } from './stripePrices';

// Default timeout for subscription API calls (30 seconds)
const DEFAULT_TIMEOUT = 30000;

/**
 * Raw subscription response from backend API.
 * Backend returns strings for tier and status, which need to be parsed to enums.
 */
interface SubscriptionApiResponse {
  practiceId: string;
  status: string;
  tier: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  isTrial: boolean;
  trialEndDate?: string;
}

/**
 * Parses the raw API response into a properly typed SubscriptionDto
 */
function parseSubscriptionResponse(data: SubscriptionApiResponse): SubscriptionDto {
  const tier = parseSubscriptionTier(data.tier);
  const tierDefaults = subscriptionTierDetails.find((t) => t.tier === tier);

  return {
    practiceId: data.practiceId,
    practiceName: data.practiceId,
    practiceAdminEmail: '',
    tier,
    status: (() => {
      const parsedStatus = parseSubscriptionStatus(data.status);

      // Only override to Trial if isTrial AND the parsed status isn't Expired
      // (backend sets isTrial=false for expired trials, but this is a safety net)
      if (data.isTrial && parsedStatus !== SubscriptionStatus.Expired) {
        return SubscriptionStatus.Trial;
      }

      return parsedStatus;
    })(),
    startDate: data.currentPeriodStart || new Date().toISOString(),
    currentPeriodEnd: data.currentPeriodEnd || '',
    trialEndDate: data.trialEndDate,
    maxPractitioners: tierDefaults?.maxPractitioners ?? 0,
    maxPatients: tierDefaults?.maxPatients ?? 0,
    features: {
      videoConferencing: false,
      advancedAnalytics: false,
      customIntegrations: false,
      prioritySupport: false,
      whiteLabeling: false,
    },
  };
}

/**
 * Subscription API client.
 */
export const subscriptionsApi = axios.create({
  baseURL: healioSpaceAPIURL + '/api/billing',
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT,
});

installSessionInterceptors(subscriptionsApi);

export const getSubscription = async (_practiceId?: string): Promise<SubscriptionDto> => {
  try {
    const response = await subscriptionsApi.get<SubscriptionApiResponse>('/subscription');

    return parseSubscriptionResponse(response.data);
  } catch (error) {
    logApiError('getSubscription', error);
    throw error;
  }
};

export const upgradeSubscription = async (
  _practiceId: string,
  newTier: number
): Promise<SubscriptionDto> => {
  try {
    // Use the update-subscription endpoint which modifies the existing Stripe
    // subscription in place (swapping the price item). This avoids creating
    // duplicate subscriptions — the old approach used create-checkout-session
    // which created a brand new Stripe subscription every time.
    // Price ids come from the billing config; throws StripePriceUnavailableError
    // (never an empty/stale id) if they can't be resolved.
    const priceId = await resolveTierPriceId(newTier);
    const response = await subscriptionsApi.post('/update-subscription', { priceId });

    return parseSubscriptionResponse(response.data);
  } catch (error) {
    logApiError('upgradeSubscription', error);
    throw error;
  }
};

export const downgradeSubscription = async (
  _practiceId: string,
  _newTier: number
): Promise<SubscriptionDto> => {
  try {
    const returnUrl = encodeURIComponent(window.location.href);
    const response = await subscriptionsApi.post('/create-portal-session?returnUrl=' + returnUrl);

    if (response.data?.url) {
      window.location.href = response.data.url;
    }
    throw new Error('Downgrade requires Stripe portal - redirecting');
  } catch (error) {
    logApiError('downgradeSubscription', error);
    throw error;
  }
};

export const cancelSubscription = async (
  _practiceId?: string,
  _immediateEffect: boolean = false
): Promise<void> => {
  try {
    await subscriptionsApi.post('/cancel-subscription');
  } catch (error) {
    logApiError('cancelSubscription', error);
    throw error;
  }
};
